// Sync-status store — the source of truth the app-bar badge and the /sync
// screen read. The sync engine WRITES here; components only READ.
//
// The badge has deliberately TWO faces (docs/plan.md, "When a queued lead
// cannot be sent"):
//   - a plain QUEUED COUNT while entries are pending / retrying, and
//   - a WARNING THAT DOES NOT CLEAR ON ITS OWN the moment anything lands in
//     `failed_permanent`. That state is the app's only silent data-loss path,
//     so it must nag until a human deals with it on the /sync screen.
//
// `failedPermanentCount` therefore lives beside `queueLen` rather than being
// folded into it, and the derived badge kind lets the permanent-failure face
// win over everything else.
import { create } from 'zustand';

export type SyncBadgeKind =
  /** Nothing queued, nothing failed. */
  | 'idle'
  /** Entries queued or awaiting backoff retry. */
  | 'queued'
  /** A drain pass is in flight. */
  | 'syncing'
  /** At least one entry is `failed_permanent` — the non-self-clearing warning. */
  | 'attention';

export interface SyncStatusState {
  /** pending + in_flight + failed_transient (i.e. "still trying"). */
  queueLen: number;
  /** failed_permanent — the count the warning badge shows. */
  failedPermanentCount: number;
  /** A drain pass is currently running. */
  syncing: boolean;
  /** `Date.now()` of the most recent successful drain (informational). */
  lastSyncAt: number | null;
  /** Last failure's human copy — a chip subtitle, never branched on. */
  lastError: string | null;

  // Engine-only writers.
  setCounts: (counts: { queueLen: number; failedPermanentCount: number }) => void;
  setSyncing: (syncing: boolean) => void;
  setLastError: (message: string | null) => void;
  markSyncedNow: () => void;
}

export const useSyncStatus = create<SyncStatusState>(set => ({
  queueLen: 0,
  failedPermanentCount: 0,
  syncing: false,
  lastSyncAt: null,
  lastError: null,

  setCounts: counts => set(counts),
  setSyncing: syncing => set({ syncing }),
  setLastError: message => set({ lastError: message }),
  markSyncedNow: () => set({ lastSyncAt: Date.now() })
}));

/** The single place the badge's visual state is derived — permanent failure wins. */
export function deriveBadgeKind(
  s: Pick<SyncStatusState, 'queueLen' | 'failedPermanentCount' | 'syncing'>
): SyncBadgeKind {
  if (s.failedPermanentCount > 0) return 'attention';
  if (s.syncing) return 'syncing';
  if (s.queueLen > 0) return 'queued';
  return 'idle';
}
